import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  Dealer,
  DealerCreateRequest,
  DealerGeocodeBatchResult,
  DealerListQuery,
  DealerListResult,
  DealerPatchRequest,
  DealerUpdateRequest,
  NearbyDealerListResult,
  NearbyDealerQuery
} from '../models/dealer.model';
import { ServiceAdvisorListResult, ServiceAdvisorRequest } from '../models/service-advisor.model';

@Injectable({ providedIn: 'root' })
export class DealerService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/dealers`;

  getDealers(query: DealerListQuery = {}): Observable<DealerListResult> {
    let params = new HttpParams();
    if (query.state) params = params.set('state', query.state);
    if (query.city) params = params.set('city', query.city);
    if (query.search) params = params.set('search', query.search);
    if (query.customerStatus) params = params.set('customerStatus', query.customerStatus);
    if (query.product) params = params.set('product', query.product);
    if (query.csatRating) params = params.set('csatRating', query.csatRating);
    if (query.driveOverVersion) params = params.set('driveOverVersion', query.driveOverVersion);
    if (query.page) params = params.set('page', query.page);
    if (query.pageSize) params = params.set('pageSize', query.pageSize);

    return this.http.get<DealerListResult>(this.baseUrl, { params });
  }

  getDealerById(dealerId: number): Observable<Dealer> {
    return this.http.get<Dealer>(`${this.baseUrl}/${dealerId}`);
  }

  createDealer(request: DealerCreateRequest): Observable<Dealer> {
    return this.http.post<Dealer>(this.baseUrl, request);
  }

  updateDealer(dealerId: number, request: DealerUpdateRequest): Observable<Dealer> {
    return this.http.put<Dealer>(`${this.baseUrl}/${dealerId}`, request);
  }

  patchDealer(dealerId: number, request: DealerPatchRequest): Observable<Dealer> {
    return this.http.patch<Dealer>(`${this.baseUrl}/${dealerId}`, request);
  }

  deactivateDealer(dealerId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${dealerId}`);
  }

  getDealerships(): Observable<unknown[]> {
    return this.http.get<unknown[]>(`${environment.apiBaseUrl}/GetDealerships`);
  }

  syncDealers(): Observable<{ synced: number; total: number }> {
    return this.http.post<{ synced: number; total: number }>(`${this.baseUrl}/sync`, {});
  }

  geocodeMissingDealers(batchSize = 200): Observable<DealerGeocodeBatchResult> {
    return this.http.post<DealerGeocodeBatchResult>(`${this.baseUrl}/geocode-missing?batchSize=${batchSize}`, {});
  }

  getNearbyDealers(query: NearbyDealerQuery): Observable<NearbyDealerListResult> {
    let params = new HttpParams()
      .set('latitude', query.latitude)
      .set('longitude', query.longitude)
      .set('radiusMiles', query.radiusMiles ?? 50);
    if (query.csatRating) params = params.set('csatRating', query.csatRating);
    if (query.customerStatus) params = params.set('customerStatus', query.customerStatus);
    if (query.driveOverVersion) params = params.set('driveOverVersion', query.driveOverVersion);

    return this.http.get<NearbyDealerListResult>(`${this.baseUrl}/nearby`, { params });
  }

  getServiceAdvisors(dealerId: number): Observable<ServiceAdvisorListResult> {
    return this.http.get<ServiceAdvisorListResult>(`${this.baseUrl}/${dealerId}/service-advisors`);
  }

  saveServiceAdvisors(dealerId: number, advisors: ServiceAdvisorRequest[]): Observable<ServiceAdvisorListResult> {
    return this.http.put<ServiceAdvisorListResult>(`${this.baseUrl}/${dealerId}/service-advisors`, advisors);
  }
}
